import { createTheme, ThemeOptions, TypographyVariantsOptions } from '@mui/material/styles';

export const marketplacePalette = {
  primary: {
    main: '#5F249F',
    dark: '#330072',
    light: '#7229C0',
    contrastText: '#FFFFFF',
    hover: '#5F249F0A',
    selected: '#5F249F14',
    focus: '#5F249F1F',
    focusVisible: '#5F249F4D',
    outlinedBorder: '#5F249F80',
  },
  secondary: {
    main: '#9C27B0',
    dark: '#7B1FA2',
    light: '#BA68C8',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#D32F2F',
    dark: '#C62828',
    light: '#EF5350',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#ED6C02',
    dark: '#E65100',
    light: '#FF9800',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#0288D1',
    dark: '#01579B',
    light: '#03A9F4',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#2E7D32',
    dark: '#1B5E20',
    light: '#4CAF50',
    contrastText: '#FFFFFF',
  },
  alert: {
    infoFill: '#E6F3FA',
    infoContent: '#013654',
  },
  background: {
    default: '#FFFFFF',
    paper: '#FFFFFF',
  },
  action: {
    hover: '#0000000A',
    selected: '#00000014',
    disabledBackground: '#0000001F',
    focus: '#0000001F',
    disabled: '#00000080',
    active: '#0000008F',
    divider: '#0000001F',
  },
  text: {
    primary: '#000000DE',
    secondary: '#00000099',
    disabled: '#00000061',
  },
  inherit: {
    white: '#FFFFFF',
    textPrimary: '#000000DE',
    grayContainer: "#F5F5FF"
  },
};

const marketplaceTypography: TypographyVariantsOptions = {
  fontFamily: 'Open Sans',
  allVariants: {
    color: marketplacePalette.text.primary,
    fontFamily: 'Open Sans',
  },
  h1: {
    fontSize: 64,
    fontWeight: 600,
    fontFamily: 'Poppins',
    lineHeight: '75px',
    letterSpacing: -0.5,
  },
  h2: {
    fontSize: 40,
    fontWeight: 600,
    fontFamily: 'Poppins',
    lineHeight: '48px',
    letterSpacing: -0.5,
  },
  h3: {
    fontSize: 32,
    fontWeight: 500,
    fontFamily: 'Poppins',
    lineHeight: '37px',
    letterSpacing: -0.5,
  },
  h4: {
    fontSize: 24,
    fontWeight: 600,
    lineHeight: '30px',
  },
  h5: {
    fontSize: 20,
    fontWeight: 600,
    lineHeight: '27px',
  },
  h6: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: '26px',
  },
  overline: {
    fontSize: 12,
    fontWeight: 700,
    lineHeight: '32px',
    letterSpacing: 0.17,
  },
  subtitle1: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '28px',
    letterSpacing: 0.15,
  },
  subtitle2: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '22px',
    letterSpacing: 0.1,
  },
  body1: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: 0.15,
  },
  body2: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: 0.17,
  },
  caption: {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: 0.4,
  },
};

// this elevations config is for dark mode
export const elevations = {
  'elevation-1': {
    background: `linear-gradient(0deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%), #121212`,
    boxShadow:
      '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 1px 3px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-2': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0.07) 100%), #121212`,
    boxShadow:
      '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 1px 5px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-3': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%, rgba(255, 255, 255, 0.08) 100%), #121212`,
    boxShadow:
      '0px 3px 3px -2px rgba(0, 0, 0, 0.2), 0px 3px 4px rgba(0, 0, 0, 0.14), 0px 1px 8px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-4': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.09) 0%, rgba(255, 255, 255, 0.09) 100%), #121212`,
    boxShadow:
      '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px rgba(0, 0, 0, 0.14), 0px 1px 10px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-5': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.1) 100%), #121212`,
    boxShadow:
      '0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 5px 8px rgba(0, 0, 0, 0.14), 0px 1px 14px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-6': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.11) 0%, rgba(255, 255, 255, 0.11) 100%), #121212`,
    boxShadow:
      '0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px rgba(0, 0, 0, 0.14), 0px 1px 18px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-7': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.11) 0%, rgba(255, 255, 255, 0.11) 100%), #121212`,
    boxShadow:
      '0px 4px 5px -2px rgba(0, 0, 0, 0.2), 0px 7px 10px 1px rgba(0, 0, 0, 0.14), 0px 2px 16px 1px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-8': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.12) 100%), #121212`,
    boxShadow:
      '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-9': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.12) 100%), #121212`,
    boxShadow:
      '0px 5px 6px -3px rgba(0, 0, 0, 0.2), 0px 9px 12px 1px rgba(0, 0, 0, 0.14), 0px 3px 16px 2px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-10': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.13) 0%, rgba(255, 255, 255, 0.13) 100%), #121212`,
    boxShadow:
      '0px 6px 6px -3px rgba(0, 0, 0, 0.2), 0px 10px 14px 1px rgba(0, 0, 0, 0.14), 0px 4px 18px 3px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-11': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.13) 0%, rgba(255, 255, 255, 0.13) 100%), #121212`,
    boxShadow:
      '0px 6px 7px -4px rgba(0, 0, 0, 0.2), 0px 11px 15px 1px rgba(0, 0, 0, 0.14), 0px 4px 20px 3px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-12': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.14) 100%), #121212`,
    boxShadow:
      '0px 7px 8px -4px rgba(0, 0, 0, 0.2), 0px 12px 17px 2px rgba(0, 0, 0, 0.14), 0px 5px 22px 4px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-13': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.14) 100%), #121212`,
    boxShadow:
      '0px 7px 8px -4px rgba(0, 0, 0, 0.2), 0px 13px 19px 2px rgba(0, 0, 0, 0.14), 0px 5px 24px 4px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-14': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.14) 100%), #121212`,
    boxShadow:
      '0px 7px 9px -4px rgba(0, 0, 0, 0.2), 0px 14px 21px 2px rgba(0, 0, 0, 0.14), 0px 5px 26px 4px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-15': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.14) 100%), #121212`,
    boxShadow:
      '0px 8px 9px -5px rgba(0, 0, 0, 0.2), 0px 15px 22px 2px rgba(0, 0, 0, 0.14), 0px 6px 28px 5px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-16': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.15) 100%), #121212`,
    boxShadow:
      '0px 8px 10px -5px rgba(0, 0, 0, 0.2), 0px 16px 24px 2px rgba(0, 0, 0, 0.14), 0px 6px 30px 5px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-17': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.15) 100%), #121212`,
    boxShadow:
      ' 0px 8px 11px -5px rgba(0, 0, 0, 0.2), 0px 17px 26px 2px rgba(0, 0, 0, 0.14), 0px 6px 32px 5px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-18': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.15) 100%), #121212`,
    boxShadow:
      ' 0px 9px 11px -5px rgba(0, 0, 0, 0.2), 0px 18px 28px 2px rgba(0, 0, 0, 0.14), 0px 7px 34px 6px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-19': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.15) 100%), #121212`,
    boxShadow:
      '0px 9px 12px -6px rgba(0, 0, 0, 0.2), 0px 19px 29px 2px rgba(0, 0, 0, 0.14), 0px 7px 36px 6px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-20': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.16) 100%), #121212`,
    boxShadow:
      '0px 10px 13px -6px rgba(0, 0, 0, 0.2), 0px 20px 31px 3px rgba(0, 0, 0, 0.14), 0px 8px 38px 7px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-21': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.16) 100%), #121212`,
    boxShadow:
      '0px 10px 13px -6px rgba(0, 0, 0, 0.2), 0px 21px 33px 3px rgba(0, 0, 0, 0.14), 0px 8px 40px 7px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-22': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.16) 100%), #121212`,
    boxShadow:
      '0px 10px 14px -6px rgba(0, 0, 0, 0.2), 0px 22px 35px 3px rgba(0, 0, 0, 0.14), 0px 8px 42px 7px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-23': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.16) 100%), #121212`,
    boxShadow:
      '0px 11px 14px -7px rgba(0, 0, 0, 0.2), 0px 23px 36px 3px rgba(0, 0, 0, 0.14), 0px 9px 44px 8px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
  'elevation-24': {
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.16) 100%), #121212`,
    boxShadow:
      '0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
  },
};

const marketplaceComponents: ThemeOptions['components'] = {
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'capitalize',
        fontWeight: 600,
      },
      sizeSmall: {
        borderRadius: '6px',
        minHeight: '30.75px',
      },
      sizeMedium: {
        minHeight: '36.5px',
      },
      sizeLarge: {
        minHeight: '42.25px',
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      sizeSmall: {
        padding: '4px',
      },
      sizeMedium: {
        padding: '12px',
      },
    },
    variants: [
      {
        props: { color: 'white' },
        style: { color: 'white' },
      },
    ],
  },
  MuiChip: {
    styleOverrides: {
      avatar: {
        color: 'currentcolor',
        marginTop: '-2px',
      },
      deleteIcon: {
        color: 'currentcolor !important',
        ":hover": {
          color: 'currentcolor !important'
        }
      }
    }
  },
  MuiLink: {
    defaultProps: {
      target: '_blank',
      rel: 'noopener',
    },
    styleOverrides: {
      root: {
        color: 'inherit',
        textDecorationColor: 'unset',
        ':not(.MuiButton-root):hover': {
          color: 'inherit',
        },
      },
    },
  },
};

export const marketplaceTheme = createTheme({
  typography: marketplaceTypography,
  components: marketplaceComponents,
  palette: marketplacePalette,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1280,
      xl: 1440,
    },
  },
});
